/**
 * useMenuMachine
 *
 * Single source of truth for all menu state and timing logic.
 * Replaces the scattered transitionTo / toggleMenu / onNavSelect functions
 * and the navVisible / contentVisible timing-proxy booleans that previously
 * lived in Menu.vue.
 *
 * States
 * ──────
 *   closed        ↔  hovering       (mouseenter / mouseleave on the container)
 *   closed | hovering  →  nav-open  (toggle click)
 *   nav-open       →  closed        (toggle click or blocker)
 *   nav-open       →  content-open  (nav item selected)
 *   content-open   →  nav-open      (toggle click)
 *   content-open   →  closed        (route change while content was open)
 *
 * Public API
 * ──────────
 *   state        – current machine state (readonly)
 *   activeView   – which content panel is showing (readonly)
 *   navActive    – drives the stagger animation inside MenuNav
 *   contentActive – controls v-if on the content wrapper
 *   motionVariant – maps machine state → @vueuse/motion variant names
 *   send(event)  – the only way to drive state changes
 */

import { computed, nextTick, readonly, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { usePageReady } from "./use-page-ready"

export type MenuState = "closed" | "hovering" | "nav-open" | "content-open"
export type MenuView = "projects" | "programmes" | "search"

export const menuOpen = ref(false)

type SendEvent =
  | "HOVER_ENTER"
  | "HOVER_LEAVE"
  | "TOGGLE"
  | "CLOSE"
  | { type: "SELECT"; view: MenuView }

// Timing constants – one place to tune all animation coordination
const EXIT_DELAY = 200 // ms: wait for CSS exit animation before collapsing box
const SHOW_DELAY = 300 // ms: wait for box open animation before revealing content

export function useMenuMachine() {
  const route = useRoute()
  const router = useRouter()

  const state = ref<MenuState>("closed")
  const activeView = ref<MenuView | null>(null)

  // The active content view is mirrored in the URL as a `?view=` query param so
  // it lives in browser history. The query is the source of truth: selecting a
  // view navigates, and the watcher below drives the machine to match.
  const queryView = computed<MenuView | null>(() => {
    const v = route.query.view
    return v === "projects" || v === "programmes" || v === "search" ? v : null
  })

  // When the toggle is used to step back from content → nav list, we clear the
  // view from the URL but want to land on `nav-open` rather than fully closing.
  // This flag tells the query watcher which resting state to use.
  let returnToNav = false

  // Push/replace the `view` query while preserving every other param (notably
  // `api` / `preview`, which the router's beforeEach guard depends on).
  function navigateView(view: MenuView | undefined, mode: "push" | "replace") {
    const query = { ...route.query }
    if (view) query.view = view
    else delete query.view
    router[mode]({ query })
  }

  // These replace the old navVisible / contentVisible booleans.
  // They're set on a delay so content only appears after the box has animated open,
  // and cleared before the box starts collapsing so the exit can play first.
  const navActive = ref(false)
  const contentActive = ref(false)

  let timer: ReturnType<typeof setTimeout> | null = null

  function clearTimer() {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  function wait(ms: number) {
    return new Promise<void>(resolve => setTimeout(resolve, ms))
  }

  async function transitionTo(next: MenuState, view?: MenuView | null) {
    clearTimer()

    const prev = state.value
    let needsDelay = false

    // Step 1: hide content/nav so their CSS exit animations can play
    if (prev === "content-open") {
      contentActive.value = false
      needsDelay = true
    }

    if (
      (prev === "nav-open" || prev === "content-open") &&
      (next === "closed" || next === "hovering")
    ) {
      navActive.value = false
      needsDelay = true
    }

    // Step 2: wait for exit animations to complete before moving the box
    if (needsDelay) await wait(EXIT_DELAY)

    // Step 3: commit the state change (drives box geometry animation in MenuContainer)
    state.value = next
    menuOpen.value = next === "nav-open" || next === "content-open"
    if (view !== undefined) activeView.value = view

    // Step 4: after the box has opened, reveal the content
    if (next === "content-open") {
      timer = setTimeout(() => {
        contentActive.value = true
      }, SHOW_DELAY)
    } else if (next === "nav-open") {
      // Always reset so the stagger animation replays on every open
      navActive.value = false
      timer = setTimeout(() => {
        navActive.value = true
      }, SHOW_DELAY)
    }

    document.body.style.overflow = next === "content-open" ? "hidden" : ""
  }

  // ─── Public event API ──────────────────────────────────────────────────────

  async function send(event: SendEvent) {
    if (event === "HOVER_ENTER") {
      if (state.value === "closed") state.value = "hovering"
      return
    }

    if (event === "HOVER_LEAVE") {
      if (state.value === "hovering") state.value = "closed"
      return
    }

    if (event === "TOGGLE") {
      switch (state.value) {
        case "closed":
        case "hovering":
          await transitionTo("nav-open")
          break
        case "nav-open":
          await transitionTo("closed")
          activeView.value = null
          break
        case "content-open":
          // Step back to the nav list: clear the view from the URL (the query
          // watcher collapses content → nav-open via the returnToNav flag).
          returnToNav = true
          navigateView(undefined, "push")
          break
      }
      return
    }

    if (event === "CLOSE") {
      if (queryView.value) {
        // A content view is open: drop it from the URL and let the watcher
        // close the panel. Pushing a clean URL means Back reopens the view.
        navigateView(undefined, "push")
      } else {
        await transitionTo("closed")
        activeView.value = null
      }
      return
    }

    if (typeof event === "object" && event.type === "SELECT") {
      // Switching between open views replaces history; opening from the nav
      // list pushes a new entry. The query watcher performs the transition.
      const mode = state.value === "content-open" ? "replace" : "push"
      navigateView(event.view, mode)
      return
    }
  }

  // ─── URL ↔ content-view sync ───────────────────────────────────────────────
  // The `?view=` query param is the source of truth for which content panel is
  // open. This watcher reacts to every query change — user selection, browser
  // back/forward, and deep links/refreshes (immediate) — and drives the machine
  // to match. The underlying routed view never remounts because route.path is
  // unchanged (App.vue keys <router-view> on route.path only).
  watch(
    queryView,
    async view => {
      if (view) {
        if (state.value === "content-open" && activeView.value === view) return
        await transitionTo("content-open", view)
      } else if (state.value === "content-open") {
        if (returnToNav) {
          returnToNav = false
          await transitionTo("nav-open", null)
        } else {
          await transitionTo("closed")
          activeView.value = null
        }
      }
    },
    { immediate: true },
  )

  // ─── Route-change watcher ──────────────────────────────────────────────────
  // Close the menu when the user navigates away while content is open.

  const { pageReady } = usePageReady()
  const menuOpenedAtPath = ref<string | null>(null)

  watch(activeView, val => {
    menuOpenedAtPath.value = val !== null ? route.path : null
  })

  watch(pageReady, async ready => {
    if (!ready) return
    const routeChanged =
      menuOpenedAtPath.value !== null && route.path !== menuOpenedAtPath.value
    if (routeChanged) {
      await nextTick()
      await send("CLOSE")
    }
  })

  // ─── Motion variant mapping ────────────────────────────────────────────────
  // Maps machine state → the variant name strings expected by @vueuse/motion
  // (both the letterform in Menu.vue and the container box use these names)

  const motionVariant = computed((): string => {
    switch (state.value) {
      case "hovering":
        return "hover"
      case "nav-open":
        return "open-small"
      case "content-open":
        return "open-large"
      default:
        return "closed"
    }
  })

  return {
    state: readonly(state),
    activeView: readonly(activeView),
    navActive: readonly(navActive),
    contentActive: readonly(contentActive),
    motionVariant,
    send,
  }
}
